/**
 * Tenant-side half of the cross-domain impersonation handoff (Sprint 3b).
 *
 * When the console mints an impersonation session it redirects the browser
 * to ${tenant}/?impersonation=<code>. This hook detects that query param
 * on app boot, exchanges the code at /auth/impersonate/exchange (which
 * sets the access_token cookie on THIS domain), strips the code from the
 * URL to avoid leaking it via history/referrer, and kicks off a normal
 * session bootstrap.
 *
 * No-op when ?impersonation is absent.
 */
import { useEffect, useState } from 'react';
import apiClient from '@/api/client';

type Status = 'idle' | 'exchanging' | 'done' | 'error';

export function useImpersonationHandoff(): { status: Status; error: string | null } {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('impersonation');
    if (!code) return;

    let cancelled = false;
    setStatus('exchanging');

    apiClient
      .post('/auth/impersonate/exchange', { code })
      .then(() => {
        if (cancelled) return;
        // Strip the code from the URL so reloads / referrers don't leak it.
        params.delete('impersonation');
        const newSearch = params.toString();
        const cleanUrl =
          window.location.pathname + (newSearch ? `?${newSearch}` : '');
        window.history.replaceState({}, '', cleanUrl);
        setStatus('done');
        // Reload so authStore picks up the newly-set cookie via /auth/me.
        window.location.reload();
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail ?? 'Impersonation handoff failed');
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { status, error };
}
